/**
 * entitlement.ts — the PAID-LAUNCH build flags (the paywall kill switch + the
 * teaser build mode). Config only: no logic, no imports, no secrets. The RULE
 * these flags feed lives next door in `src/core/entitlement.ts`, pure + tested
 * (the M111 `shouldFrontAccountGate` shape).
 *
 * Mirrors two established house idioms:
 *  - the SUPABASE_ENABLED kill switch in config/supabase.ts (one flag, instant
 *    rollback to the pre-feature behaviour);
 *  - the VITE_WORKER_WSS_URL build variable in config/network.ts (the only
 *    existing `import.meta.env` precedent — read with a `??`-style default so a
 *    plain `npm run build` always produces the normal game).
 */

/**
 * KILL SWITCH — the paid gate. **FALSE BY DEFAULT: nothing is gated until this
 * is deliberately flipped on.** Setting it false restores EXACTLY today's free
 * behaviour: no purchase screen is ever fronted, no launch verb is guarded, and
 * every screen renders as it does now. It is the rollback lever if the gate
 * misfires in production, and it is also the FREE-CONVERSION trigger the launch
 * plan reserves — the day the game goes free, this one line does it.
 *
 * DELIBERATELY SEPARATE from SUPABASE_ENABLED. Those two switches answer
 * different questions and must be flippable independently:
 *   - SUPABASE_ENABLED off = "there are no accounts at all" (pre-M100).
 *   - ENTITLEMENT_ENABLED off = "accounts still work, the game just isn't paid".
 * Going free must NOT cost players their Racer ID, their cloud Skill Points or
 * the leaderboard, so the free switch cannot be the account switch.
 *
 * Annotated `: boolean` on purpose (unlike SUPABASE_ENABLED): without it TS
 * infers the literal type `false` and every call site's paid branch reads as
 * dead code to the compiler, which makes flipping the flag a multi-file edit.
 */
export const ENTITLEMENT_ENABLED: boolean = true;

/**
 * The three teaser builds. ONE string, three values — never two booleans: the
 * portal rule below must be impossible to half-apply.
 *
 *  - `'off'`    — THE FULL GAME. Today's behaviour, byte-identical. The default
 *                 for every normal build (dribbleracer.com, iOS, Android).
 *  - `'web'`    — the free teaser a COLD VISITOR lands on at dribbleracer.com:
 *                 NYC only, Quick Race only, online + shop locked, accounts off,
 *                 a soft replay cap, and storefront CTAs SHOWN (this build's job
 *                 is to sell the full game).
 *  - `'portal'` — the CrazyGames / Poki bundle. Same teaser content, but ALL
 *                 PURCHASE UI IS SUPPRESSED: portal requirements (Poki's in
 *                 particular) forbid selling out of an embedded game. A portal
 *                 build that shows a Buy button is a submission rejection, so
 *                 the suppression is a mode value rather than a per-screen
 *                 conditional somebody can forget on screen twelve.
 */
export type TeaserMode = 'off' | 'web' | 'portal';

const TEASER_MODES: readonly string[] = ['off', 'web', 'portal'];

/** Anything unrecognised (typo, unset, non-string) reads as the FULL GAME —
 *  a broken build variable must never silently ship a crippled bundle. */
function readTeaserMode(raw: unknown): TeaserMode {
  return typeof raw === 'string' && TEASER_MODES.includes(raw) ? (raw as TeaserMode) : 'off';
}

/** Set at build time: `VITE_TEASER=web npm run build` / `VITE_TEASER=portal …`. */
export const TEASER_MODE: TeaserMode = readTeaserMode(import.meta.env.VITE_TEASER);

/** Is this a cut-down build of any kind? (Teasers must NEVER paywall.) */
export function isTeaser(mode: TeaserMode = TEASER_MODE): boolean {
  return mode !== 'off';
}

/** Is this the portal bundle, i.e. must every purchase affordance stay hidden? */
export function isPortalBuild(mode: TeaserMode = TEASER_MODE): boolean {
  return mode === 'portal';
}

/**
 * The teaser's SOFT replay cap — after this many finished races the purchase
 * screen is fronted WITH a "keep playing" escape. Soft on purpose: a hard wall
 * tanks the session-length metric portals grade you on, and the counter is
 * device-local (an incognito window resets it), so a hard wall buys nothing.
 * 0 disables the cap entirely.
 */
export const TEASER_FREE_RACES = 5;
